import { useEffect, useState } from "react";
import { useContract } from "./useContract";

export interface DecryptHighlight {
  voteId: number;
  title: string;
  winner: string;
  votes: number;
  totalVotes: number;
  share: number;
  timestamp: string;
}

export interface PollStats {
  totalPolls: number;
  activePolls: number;
  endedPolls: number;
  decryptedPolls: number;
  totalVoters: number;
  averageVoters: number;
  highlight: DecryptHighlight | null;
}

interface PollInsightsState {
  loading: boolean;
  error: string | null;
  stats: PollStats | null;
}

const EMPTY_STATE: PollInsightsState = {
  loading: true,
  error: null,
  stats: null,
};

export function usePollInsights(refreshKey: number) {
  const contractPromise = useContract();
  const [state, setState] = useState<PollInsightsState>(EMPTY_STATE);

  useEffect(() => {
    let cancelled = false;

    const loadInsights = async () => {
      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        if (!contractPromise) {
          setState({
            loading: false,
            error: "Connect your wallet to load insights",
            stats: null,
          });
          return;
        }

        const contract = await contractPromise;
        if (!contract) {
          setState({
            loading: false,
            error: "Contract not available on this network",
            stats: null,
          });
          return;
        }

        const count = Number(await contract.getVoteCount());
        let activePolls = 0;
        let endedPolls = 0;
        let decryptedPolls = 0;
        let totalVoters = 0;
        let highlight: DecryptHighlight | null = null;

        for (let id = count - 1; id >= 0; id -= 1) {
          const voteInfo = await contract.getVote(id);
          const status = await contract.getVoteStatus(id);

          const isActive = status[0];
          const isEnded = status[1];
          const isDecrypted = status[2];
          const recordVoters = Number(voteInfo[6]);

          totalVoters += recordVoters;
          if (isActive) {
            activePolls += 1;
          } else if (isEnded) {
            endedPolls += 1;
          }
          if (isDecrypted || voteInfo[5]) {
            decryptedPolls += 1;
          }

          if (!highlight && (isDecrypted || voteInfo[5])) {
            const resultsRaw = await contract.getResults(id);
            const results = resultsRaw.map((value: bigint) => Number(value));
            const totalResults = results.reduce(
              (sum: number, value: number) => sum + value,
              0
            );
            const leadingIndex =
              results.length > 0
                ? results.reduce(
                    (bestIndex: number, currentValue: number, currentIndex: number, arr: number[]) =>
                      currentValue > arr[bestIndex] ? currentIndex : bestIndex,
                    0
                  )
                : 0;

            const winner = voteInfo[2][leadingIndex] ?? "Option";
            const winnerVotes = results[leadingIndex] ?? 0;
            const share = totalResults > 0 ? winnerVotes / totalResults : 0;

            highlight = {
              voteId: id,
              title: voteInfo[0],
              winner,
              votes: winnerVotes,
              totalVotes: totalResults,
              share,
              timestamp: new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
            };
          }
        }

        const stats: PollStats = {
          totalPolls: count,
          activePolls,
          endedPolls,
          decryptedPolls,
          totalVoters,
          averageVoters: count > 0 ? Math.round((totalVoters / count) * 100) / 100 : 0,
          highlight,
        };

        if (!cancelled) {
          setState({ loading: false, error: null, stats });
        }
      } catch (err: any) {
        if (!cancelled) {
          setState({
            loading: false,
            error: err?.message || "Failed to load insights",
            stats: null,
          });
        }
      }
    };

    void loadInsights();

    return () => {
      cancelled = true;
    };
  }, [contractPromise, refreshKey]);

  return state;
}

